/**
 * Staff Sign-In Routing
 *
 * Trusscore staff authenticate against the company Microsoft 365 tenant, which is
 * brokered into Keycloak as an identity provider. That provider is deliberately
 * hidden from Keycloak's own login page (hideOnLogin=true) so customers never see
 * a Microsoft button — `kc_idp_hint` is the only way in, and the sign-in modal
 * reaches it by recognising the email domain the user typed.
 *
 * This is PRESENTATION AND ROUTING ONLY. Matching a domain here decides which
 * login screen someone is sent to; it grants nothing. A @trusscore.com address is
 * not proof of employment — Keycloak (and the roles it issues) remains the sole
 * authority on who is staff and what they may do. Never use `isStaffEmail()` to
 * gate data, admin UI, or any API behaviour.
 */

/**
 * Email domains routed to the Microsoft 365 broker instead of the password form.
 * Lower-case, bare domain (no `@`). Add further staff/tenant domains here.
 */
export const STAFF_EMAIL_DOMAINS: readonly string[] = ['trusscore.com'];

/** Keycloak IdP alias for the Microsoft 365 broker, passed as kc_idp_hint. */
export const STAFF_IDP_ALIAS = 'oidc';

/**
 * Shown once a staff address is recognised, so the redirect isn't a surprise.
 * An i18n key rather than text — the modal that renders it translates it.
 */
export const STAFF_SIGN_IN_NOTICE_KEY = 'auth:staffNotice';

/**
 * True when `email`'s domain is one of STAFF_EMAIL_DOMAINS. Case-insensitive and
 * whitespace-tolerant; exact domain match only, so sub.trusscore.com does not
 * count unless it is listed. Returns false for anything that isn't `local@domain`.
 */
export const isStaffEmail = (email: string): boolean => {
  const at = email.trim().toLowerCase().lastIndexOf('@');
  if (at < 1) return false;
  const domain = email.trim().toLowerCase().slice(at + 1);
  return domain !== '' && STAFF_EMAIL_DOMAINS.includes(domain);
};
